import { hasChildFieldSet, isDataField, type FormBuilderService, type FormStore, type TokenStore } from '@cognitoforms/form-builder';
import type { ContractHandlers } from '@cognitoforms/builder-types';
import { createLegacySettingsHandlers } from './handlers/legacy-settings-handlers';
import { createLegacyFieldHandlers } from './handlers/legacy-field-handlers';
import { createLegacyWorkflowHandlers } from './handlers/legacy-workflow-handlers';
import { createLegacyActionEmailHandlers } from './handlers/legacy-action-email-handlers';
import type { LegacyForm } from './legacy-form';
import type { ExoWebEntity, ExoWebPropertyChangeEventArgs, ExoWebType, TypedExoWebEntity } from 'src/views/admin/utils/exoweb';
import type { Field } from '@cognitoforms/types/server-types/core/model/forms/definition/field';
import type { Field as OldField } from '@cognitoforms/types/server-types/model/field';
import type { WorkflowAction as OldAction } from '@cognitoforms/types/server-types/forms/model/workflow-action';
import type { ActionEmail } from '@cognitoforms/types/server-types/core/model/forms/definition/action-email';
import { FieldType } from '@cognitoforms/types/server-types/core/model/forms/definition/field-type';
import type { TypeMeta } from '@cognitoforms/types/server-types/model/type-meta';
import { FormType } from '@cognitoforms/types/server-types/forms/model/form-type';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Cognito: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const $: any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LegacyHandler = (legacyForm: LegacyForm, ...args: any[]) => void;

export type LegacyHandlers = Partial<{ [M in keyof ContractHandlers]: LegacyHandler }>;

const legacyHandlers: LegacyHandlers = {
	...createLegacySettingsHandlers(),
	...createLegacyFieldHandlers(),
	...createLegacyWorkflowHandlers(),
	...createLegacyActionEmailHandlers()
};

const getLegacyForm = (): LegacyForm | null => Cognito.Forms?.model?.currentForm ?? null;

/**
 * Wraps a FormBuilderService so every contract-method call also mirrors its method
 * name and args onto the legacy ExoWeb form (Cognito.Forms.model.currentForm),
 * which the still-MsAjax-rendered parts of the builder read from.
 *
 * Coverage grows one handler at a time as areas migrate to the new model
 */
export const createLegacyMirroredBuilder = (builder: FormBuilderService): FormBuilderService => {
	return new Proxy(builder, {
		get(target, prop, receiver) {
			const original = Reflect.get(target, prop, receiver);
			const legacyHandler = legacyHandlers[prop as keyof ContractHandlers];
			if (typeof original !== 'function' || !legacyHandler)
				return original;

			return async (...args: unknown[]) => {
				const result = await original.apply(target, args);
				const legacyForm = getLegacyForm();
				if (legacyForm) {
					const leadingArgs = args.slice(0, -1);
					legacyHandler(legacyForm, ...leadingArgs, result);
				}
				return result;
			};
		}
	});
};

const getFieldPathByUuid = (uuid: string) => {
	const element = $('[data-uuid=' + uuid + ']')[0];
	if (!element)
		return;

	return $(element).get_idScope();
};

const getFieldSetByInternalName = (formStore: FormStore, internalNamePath: string) => {
	const segments = internalNamePath.split('.').slice(1);
	let fieldSet = formStore.form.FieldSets.find(fs => fs.Id === formStore.rootFieldSetId);
	let idPath = formStore.rootFieldSetId;
	for (const segment of segments) {
		if (!fieldSet)
			return;

		const field = fieldSet?.Fields.find(f => isDataField(f) && f.FieldName === segment);
		if (!field)
			return;

		idPath = idPath + '.' + field.Index;
		fieldSet = formStore.form.FieldSets.find(fs => fs.Id === idPath);
	}

	return fieldSet;
};

const getField = (formStore: FormStore, field: TypedExoWebEntity<OldField> & { _uuid: string }) => {
	const idScope = getFieldPathByUuid(field._uuid);
	const fieldSetId = formStore.rootFieldSetId + (idScope ? '.' + idScope : '');
	return formStore.getField(fieldSetId, field.get_Index());
};

const syncCollection = <T>(
	type: ExoWebType,
	propertyName: string,
	getCollection: (sender: ExoWebEntity, args: ExoWebPropertyChangeEventArgs) => T[] | undefined,
	identifier: string & keyof T,
	onCreate?: (newItem: T, oldItem: any) => void
) => {
	type.property(propertyName).addChanged((sender, args) => {
		if (args.collectionChanged && args.changes) {
			const collection = getCollection(sender, args);
			if (!collection)
				return;

			// Order won't matter until the new model controls the display for the object in question
			const getter = 'get_' + identifier;
			if (args.changes.length === 2) {
				const newIds = args.changes.filter(c => c.newItems).flatMap(c => c.newItems).map(i => i[getter]());
				const oldIds = args.changes.filter(c => c.oldItems).flatMap(c => c.oldItems).map(i => i[getter]());
				const isReorder = newIds.length === 1 && oldIds.length === 1 && newIds[0] === oldIds[0];
				if (isReorder)
					return;
			}

			for (const change of args.changes) {
				if (change.newItems) {
					for (const item of change.newItems) {
						const newItem = { [identifier]: item[getter]() } as unknown as T;
						collection.push(newItem);

						if (onCreate)
							onCreate(newItem, item);
					}
				}
				if (change.oldItems) {
					for (const item of change.oldItems) {
						const toRemove = collection.findIndex(i => i[identifier] === item['get_' + identifier]());
						if (toRemove !== -1)
							collection.splice(toRemove, 1);
					}
				}
			}
		}
	});
};

const compareCollections = <NewT, OldT, ExoT extends TypedExoWebEntity<OldT>>(oldCollection: ExoT[], newCollection: NewT[], identifier: string & keyof NewT & keyof OldT) => {
	const oldIds = oldCollection.map(i => i['get_' + identifier]());
	const newIds = newCollection.map(i => i[identifier]);

	const diff = new Set(oldIds).symmetricDifference(new Set(newIds));
	return diff.size === 0;
};

const collectFieldSets = (typeId: string, type: TypedExoWebEntity<TypeMeta>, fieldSets: { id: string, typeMeta: TypedExoWebEntity<TypeMeta> }[]) => {
	fieldSets.push({ id: typeId, typeMeta: type });
	for (const field of type.get_Fields()) {
		if (field.get_ChildType() && field.get_FieldType() !== Cognito.FieldType('RatingScale'))
			collectFieldSets(typeId + '.' + field.get_Index(), field.get_ChildType(), fieldSets);
	}
};

export const bindCollections = (formStore: FormStore) => {
	const legacyForm = getLegacyForm()!;

	// Remove page breaks and content fields from new model fields to avoid index collisions
	// TODO: Remove this once field indexes are reserved for these on the old model
	for (const fs of formStore.form.FieldSets) {
		const newFields = [];
		for (const f of fs.Fields) {
			if (f.FieldType !== FieldType.Content && f.FieldType !== FieldType.PageBreak)
				newFields.push(f);
		}
		fs.Fields = newFields;
	}

	// New forms initialize emails independently. Ensure ids line up
	const emails = legacyForm.get_Actions().flatMap(a => a.get_Emails());
	if (emails.length === 1)
		formStore.form.Workflow.Actions.flatMap(a => a.Emails)[0].Id = emails[0].get_Id();

	// Sync fields from containers
	const typeMeta = legacyForm.meta.type.baseType!;
	typeMeta.property('Fields').addChanged((sender, args) => {
		if (!args.changes)
			return;

		const field = sender as TypedExoWebEntity<OldField>;
		const fs = getFieldSetByInternalName(formStore, field.get_InternalName());
		if (!fs)
			return;

		// Since changes are sourced from the legacy model, do not use builder methods to avoid redundant propagation back
		for (const change of args.changes) {
			if (change.newItems) {
				for (const item of change.newItems) {
					const newFieldType: FieldType = item.get_elementType().code;
					if (newFieldType === FieldType.Content || newFieldType === FieldType.PageBreak)
						continue;

					const newField = { Index: item.get_Index(), FieldType: item.get_elementType().code, FieldName: item.get_InternalName(), IsProtected: item.get_IsProtected() };
					if (hasChildFieldSet(newField)) {
						newField.FieldSetId = fs.Id + '.' + newField.Index;
						formStore.form.FieldSets.push({ Id: newField.FieldSetId, Fields: [], NextFieldIndex: 0 });
					}
					fs.Fields.push(newField as unknown as Field); // Only include required properties
					fs.NextFieldIndex++;
				}
			}
			if (change.oldItems) {
				for (const item of change.oldItems) {
					const toDelete = fs.Fields.findIndex(f => f.Index === item.get_Index());
					if (toDelete === -1)
						continue;

					const field = fs.Fields.splice(toDelete, 1)[0]!;
					if (!hasChildFieldSet(field))
						continue;

					formStore.form.FieldSets = formStore.form.FieldSets.filter(fs => fs.Id !== field.FieldSetId && !fs.Id.startsWith(`${field.FieldSetId}.`));
				}
			}
		}
	});

	// Sync field internal name
	const fieldType: ExoWebType = Cognito.Field.meta.type;
	fieldType.property('InternalName').addChanged((sender: any, args) => {
		const field = getField(formStore, sender);
		if (field && isDataField(field))
			field.FieldName = args.newValue;
	});

	fieldType.property('IsProtected').addChanged((sender: any, args) => {
		const field = getField(formStore, sender);
		if (field && isDataField(field))
			field.IsProtected = args.newValue;
	});

	const formType = legacyForm.meta.type;
	syncCollection(formType, 'Actions', () => formStore.form.Workflow.Actions, 'Id', (newAction, oldAction) => {
		const exoAction: TypedExoWebEntity<OldAction> = oldAction;
		newAction.Emails = exoAction?.get_Emails().map(e => ({ Id: e.get_Id() } as unknown as ActionEmail));
	});
	syncCollection(formType, 'DocumentTemplates', () => formStore.form.Workflow.DocumentTemplates, 'Number');
	syncCollection(formType, 'Roles', () => formStore.form.Workflow.Roles, 'Id');
	syncCollection(formType, 'EntryStatuses', () => formStore.form.Workflow.Statuses, 'Id');

	const actionType: ExoWebType = formType.property('Actions').get_jstype().meta.type;
	syncCollection<ActionEmail>(actionType, 'Emails', (sender) => {
		const action = sender as TypedExoWebEntity<OldAction>;
		const actionId = action.get_Id();
		return formStore.form.Workflow.Actions.find(a => a.Id === actionId)?.Emails;
	}, 'Id');

	// TODO: Will round up the other devtools we've added in a followup
	Cognito.Debug ??= {};
	Cognito.Debug.compareModels = () => {
		if (!compareCollections(legacyForm.get_Actions(), formStore.form.Workflow.Actions, 'Id'))
			console.log('Actions different');

		if (!compareCollections(legacyForm.get_Roles(), formStore.form.Workflow.Roles, 'Id'))
			console.log('Roles different');

		if (!compareCollections(legacyForm.get_EntryStatuses(), formStore.form.Workflow.Statuses, 'Id'))
			console.log('Statuses different');

		if (!compareCollections(legacyForm.get_DocumentTemplates(), formStore.form.Workflow.DocumentTemplates, 'Number'))
			console.log('Documents different');

		for (const action of legacyForm.get_Actions()) {
			const newAction = formStore.form.Workflow.Actions.find(a => a.Id === action.get_Id())!;
			if (!compareCollections(action.get_Emails(), newAction.Emails, 'Id'))
				console.log(`Emails different for action ${newAction.Id}`);
		}

		const fieldSets: Parameters<typeof collectFieldSets>[2] = [];
		collectFieldSets('Form', legacyForm, fieldSets);
		for (const { id, typeMeta } of fieldSets) {
			const fieldSet = formStore.getFieldSet(id)!;
			if (!fieldSet) {
				console.log(`Field set ${id} not found`);
				continue;
			}

			if (!compareCollections(typeMeta.get_Fields(), fieldSet.Fields, 'Index'))
				console.log(`Fields not equal on set ${id}`);
		}
	};
};

export const setupBindings = (formStore: FormStore, tokenStore: TokenStore) => {
	const legacyForm = getLegacyForm()!;

	const syncTokens = () => {
		tokenStore.tokens = [...legacyForm.get_tokens()];
	};
	const syncProtectedTokens = () => {
		tokenStore.protectedTokens = [...legacyForm.get_protectedTokens()];
	};

	legacyForm.meta.property('tokens').addChanged(syncTokens);
	legacyForm.meta.property('protectedTokens').addChanged(syncProtectedTokens);
	syncTokens();
	syncProtectedTokens();

	// TODO: Remove when Person Form Settings are fully migrated to Vue and every FormType change goes through updateFormSettings
	legacyForm.meta.property('FormType').addChanged(() => {
		formStore.form.Settings.FormType = legacyForm.get_FormType()?.get_Name() ?? FormType.Standard;
	});
};

export interface SaveFormResponse {
	Format?: string | null;
	EntrySummaryCustomized: boolean;
}

export const applySaveResponse = (store: FormStore, data: SaveFormResponse) => {
	const lookupForm = store.form.Settings.LookupForm;
	lookupForm.EntrySummary = data.Format || null;
	lookupForm.EntrySummaryCustomized = data.EntrySummaryCustomized;

	const legacyForm = getLegacyForm();
	if (legacyForm) {
		legacyForm.set_Format(lookupForm.EntrySummary);
		legacyForm.set_EntrySummaryCustomized(lookupForm.EntrySummaryCustomized);
	}
};

export const applyDefaultedEntrySummary = (store: FormStore, format: string | null) => {
	const lookupForm = store.form.Settings.LookupForm;
	lookupForm.EntrySummary = format || null;
	lookupForm.EntrySummaryCustomized = false;
};
